// [GROUNDWATER]'s last 4 columns (NodeElev/BottomElev/WaterTableElev/
// UpperMoisture) are optional overrides — "*" (or omitted) means "let the
// engine compute this instead," modeled as `null` rather than 0. Unlike
// most other optional-trailing-column sections in this app, blank and zero
// are NOT the same thing here.

import { parseNum } from "./parseNum.ts";
import { warningText } from "../../engine/engineWarnings.ts";
import { writeOrDeleteSection } from "./writeOrDeleteSection.ts";

export interface Groundwater {
  subcatchment: string;
  aquifer: string;
  node: string;
  surfaceElev: number;
  a1: number;
  b1: number;
  a2: number;
  b2: number;
  a3: number;
  fixedDepth: number;
  nodeElev: number | null;
  bottomElev: number | null;
  waterTableElev: number | null;
  upperMoisture: number | null;
}

export type GwFlowKind = "LATERAL" | "DEEP";

export interface GwFlowExpression {
  subcatchment: string;
  kind: GwFlowKind;
  expression: string;
}

/** A token present but not a valid number is treated as unset too (closer to "unset" than a silently-wrong 0), but warns so it doesn't disappear unnoticed. */
function optionalNum(
  row: string[],
  index: number,
  context: string,
  warnings?: Set<string>
): number | null {
  const token = row[index];
  if (!token || token === "*") return null;
  const n = Number(token);
  if (!Number.isFinite(n)) {
    warnings?.add(
      warningText(
        "engineWarning.invalidNumberUnset",
        `${context}: "${token}" is not a valid number — treated as unset ("*").`,
        { context, token }
      )
    );
    return null;
  }
  return n;
}

export function readGroundwater(
  raw: ReadonlyMap<string, string[][]>,
  warnings?: Set<string>
): Groundwater[] {
  return (raw.get("GROUNDWATER") ?? [])
    .filter((row) => row[0] && row[1] && row[2])
    .map((row) => {
      const ctx = (field: string) => `[GROUNDWATER] "${row[0]}" ${field}`;
      return {
        subcatchment: row[0],
        aquifer: row[1],
        node: row[2],
        surfaceElev: parseNum(row[3], 0, ctx("Surface Elevation"), warnings),
        a1: parseNum(row[4], 0, ctx("A1"), warnings),
        b1: parseNum(row[5], 0, ctx("B1"), warnings),
        a2: parseNum(row[6], 0, ctx("A2"), warnings),
        b2: parseNum(row[7], 0, ctx("B2"), warnings),
        a3: parseNum(row[8], 0, ctx("A3"), warnings),
        fixedDepth: parseNum(row[9], 0, ctx("Fixed Depth"), warnings),
        nodeElev: optionalNum(row, 10, ctx("Node Elevation"), warnings),
        bottomElev: optionalNum(row, 11, ctx("Bottom Elevation"), warnings),
        waterTableElev: optionalNum(row, 12, ctx("Water Table Elevation"), warnings),
        upperMoisture: optionalNum(row, 13, ctx("Upper Moisture"), warnings),
      };
    });
}

export function writeGroundwater(
  raw: ReadonlyMap<string, string[][]>,
  groundwater: Groundwater[]
): Map<string, string[][]> {
  const rows = groundwater.map((g) => [
    g.subcatchment,
    g.aquifer,
    g.node,
    String(g.surfaceElev),
    String(g.a1),
    String(g.b1),
    String(g.a2),
    String(g.b2),
    String(g.a3),
    String(g.fixedDepth),
    g.nodeElev === null ? "*" : String(g.nodeElev),
    g.bottomElev === null ? "*" : String(g.bottomElev),
    g.waterTableElev === null ? "*" : String(g.waterTableElev),
    g.upperMoisture === null ? "*" : String(g.upperMoisture),
  ]);
  return writeOrDeleteSection(raw, "GROUNDWATER", rows);
}

export function readGwFlowExpressions(raw: ReadonlyMap<string, string[][]>): GwFlowExpression[] {
  const rows = raw.get("GWF") ?? [];
  return rows
    .filter((row) => row[0] && row[1] && row.length > 2)
    .map((row) => ({
      subcatchment: row[0],
      kind: row[1].toUpperCase().startsWith("DEEP") ? "DEEP" : "LATERAL",
      expression: row.slice(2).join(" "),
    }));
}

export function writeGwFlowExpressions(
  raw: ReadonlyMap<string, string[][]>,
  expressions: GwFlowExpression[]
): Map<string, string[][]> {
  // A blank expression still writes as "0" rather than dropping the row —
  // no local draft state backs this editor, so dropping on empty would
  // erase the row the instant its text field is cleared.
  const rows = expressions.map((e) => [
    e.subcatchment,
    e.kind,
    ...(e.expression.trim() || "0").split(/\s+/),
  ]);
  return writeOrDeleteSection(raw, "GWF", rows);
}
